
import React, { useState, useEffect } from 'react'
import { Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Sidebar from '../components/Sidebar'
import { useResources } from '@/hooks/useResources'
import { useUserResources } from '@/hooks/useUserResources'
import { useUserInspirations } from '@/hooks/useUserInspirations'
import ResourceCard from '@/components/resources/ResourceCard'
import EmptyResourceState from '@/components/resources/EmptyResourceState'
import ResourceFilters from '@/components/resources/ResourceFilters'
import { useModulesForDropdown } from '@/hooks/useModulesForDropdown'
import { supabase } from '@/integrations/supabase/client'

const domains = [
  { value: 'self', label: 'Self' },
  { value: 'wealth', label: 'Wealth' },
  { value: 'social', label: 'Social' }
]

const Resources = () => {
  const [selectedDomain, setSelectedDomain] = useState('')
  const [selectedModule, setSelectedModule] = useState('')
  const [selectedView, setSelectedView] = useState('')
  const [resourceInspirations, setResourceInspirations] = useState<any[]>([])
  
  const { resources, loading: adminLoading } = useResources()
  const { 
    userResources, 
    loading: userLoading, 
    addResourceToMyResources, 
    updateResourceStatus,
    isResourceAdded,
    getActiveResources,
    getPriorityResources,
    getArchivedResources
  } = useUserResources()
  const { inspirations } = useUserInspirations()
  const { modules: allModules, loading: modulesLoading } = useModulesForDropdown()

  // Filter modules by selected domain using actual database categories
  const availableModules = allModules.filter(module => {
    if (!selectedDomain) return []
    
    // Map domain selection to actual module categories from database
    if (selectedDomain === 'wealth') {
      return module.category === 'Wealth'
    } else if (selectedDomain === 'self') {
      return module.category === 'Self'
    } else if (selectedDomain === 'social') {
      return module.category === 'Social'
    }
    return false
  })

  // Fetch resource data for inspirations
  useEffect(() => {
    const fetchResourceInspirations = async () => {
      const resourceInspirs = inspirations.filter(insp => insp.resource_id && !insp.added_to_my_list)
      
      if (resourceInspirs.length > 0) {
        const { data: adminResourcesData } = await supabase
          .from('admin_resources')
          .select('*')
          .in('id', resourceInspirs.map(insp => insp.resource_id))

        const { data: schoolResourcesData } = await supabase
          .from('school_resources')
          .select('*')
          .in('id', resourceInspirs.map(insp => insp.resource_id))

        const allResourcesData = [
          ...(adminResourcesData || []).map(r => ({ ...r, source: 'admin' })),
          ...(schoolResourcesData || []).map(r => ({ ...r, source: 'school' }))
        ]

        const inspoResources = resourceInspirs.map(insp => {
          const resourceData = allResourcesData.find(res => res.id === insp.resource_id)
          return { ...insp, resourceData }
        }).filter(insp => insp.resourceData)

        setResourceInspirations(inspoResources)
      } else {
        setResourceInspirations([])
      }
    }

    fetchResourceInspirations()
  }, [inspirations])

  const handleDomainChange = (value: string) => {
    setSelectedDomain(value)
    setSelectedModule('') // Reset module when domain changes
  }

  const handleAddResource = async (resourceId: string) => {
    const resource = resources.find(r => r.id === resourceId)
    if (resource) {
      await addResourceToMyResources(resource)
    }
  }

  const handleArchiveResource = async (resourceId: string) => {
    await updateResourceStatus(resourceId, 'archived')
  }

  const handleAddInspirationResource = async (inspiration: any) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      // Add to user resources
      await supabase.from('user_resources').insert({
        user_id: session.user.id,
        title: inspiration.resourceData.title,
        description: inspiration.resourceData.description,
        module: inspiration.resourceData.module,
        domain: inspiration.resourceData.domain,
        file_url: inspiration.resourceData.file_url,
        priority: false
      })

      // Mark inspiration as added
      await supabase
        .from('user_inspirations')
        .update({ added_to_my_list: true })
        .eq('id', inspiration.id)

    } catch (error) {
      console.error('Error adding inspiration resource:', error)
    }
  }

  const getFilteredAdminResources = () => {
    let filtered = resources.filter(resource => resource.source === 'admin')

    // Filter by domain
    if (selectedDomain) {
      filtered = filtered.filter(resource => resource.domain === selectedDomain)
    }

    // Filter by module
    if (selectedModule) {
      filtered = filtered.filter(resource => resource.module === selectedModule)
    }

    return filtered
  }

  const getFilteredUserResources = (resourceList: any[]) => {
    let filtered = [...resourceList]

    // Filter by domain
    if (selectedDomain) {
      filtered = filtered.filter(resource => resource.domain === selectedDomain)
    }

    // Filter by module
    if (selectedModule) {
      filtered = filtered.filter(resource => resource.module === selectedModule)
    }

    return filtered
  }

  const activeResources = getFilteredUserResources(getActiveResources())
  const priorityResources = getFilteredUserResources(getPriorityResources())
  const regularUserResources = activeResources.filter(resource => !resource.priority)
  const archivedResources = getFilteredUserResources(getArchivedResources())
  const discoverResources = getFilteredAdminResources()

  const renderMyResourcesView = () => (
    <div className="grid grid-cols-2 gap-6">
      {/* Priority Resources */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Priority Resources</h2>
        <div className="space-y-3">
          {priorityResources.length > 0 ? (
            priorityResources.map(resource => (
              <ResourceCard 
                key={resource.id} 
                resource={resource} 
                showArchiveButton={true}
                onArchiveResource={handleArchiveResource}
              />
            ))
          ) : (
            <EmptyResourceState title="Priority Resources" message="No priority resources" />
          )}
        </div>
      </div>

      {/* Regular Resources */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">My Resources</h2>
        <div className="space-y-3">
          {regularUserResources.length > 0 ? (
            regularUserResources.map(resource => (
              <ResourceCard 
                key={resource.id} 
                resource={resource}
                showArchiveButton={true}
                onArchiveResource={handleArchiveResource}
              />
            ))
          ) : (
            <EmptyResourceState title="My Resources" message="No resources yet" />
          )}
        </div>
      </div>
    </div>
  )

  const renderDiscoverResourcesView = () => (
    <div className="grid grid-cols-3 gap-6">
      {/* Discover Resources (Admin) */}
      <div className="col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Discover Resources</h2>
        <div className="space-y-3">
          {discoverResources.length > 0 ? (
            discoverResources.map(resource => (
              <ResourceCard 
                key={resource.id} 
                resource={resource} 
                showBadge 
                showAddButton 
                isAdded={isResourceAdded(resource.id, resource.title)}
                onAddResource={handleAddResource}
              />
            ))
          ) : (
            <EmptyResourceState title="Discover Resources" message="No discover resources available" />
          )}
        </div>
      </div>

      {/* Inspo Resources */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Inspo Resources</h2>
        <div className="space-y-3">
          {resourceInspirations.length > 0 ? (
            resourceInspirations.map(inspiration => (
              <div key={inspiration.id} className="border-l-4 border-l-purple-500 bg-gradient-to-r from-purple-50 to-transparent">
                <ResourceCard 
                  resource={{
                    ...inspiration.resourceData,
                    badge: 'Recommended'
                  }}
                  showBadge 
                  showAddButton 
                  isAdded={false}
                  onAddResource={() => handleAddInspirationResource(inspiration)}
                />
              </div>
            ))
          ) : (
            <EmptyResourceState title="Inspo Resources" message="Complete quizzes to get personalized recommendations" />
          )}
        </div>
      </div>
    </div>
  )

  const renderArchivedResourcesView = () => (
    <div className="max-w-4xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Archived Resources</h2>
      <div className="space-y-3">
        {archivedResources.length > 0 ? (
          archivedResources.map(resource => (
            <ResourceCard key={resource.id} resource={resource} />
          ))
        ) : (
          <EmptyResourceState title="Archived Resources" message="No archived resources yet" />
        )}
      </div>
    </div>
  )

  if (adminLoading || userLoading) {
    return (
      <div className="flex min-h-screen bg-gray-50">
        <Sidebar />
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center py-8">Loading resources...</div>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header with filters */}
          <div className="flex items-center justify-between mb-6">
            <ResourceFilters
              selectedDomain={selectedDomain}
              selectedModule={selectedModule}
              selectedView={selectedView}
              onDomainChange={handleDomainChange}
              onModuleChange={setSelectedModule}
              onViewChange={setSelectedView}
              availableModules={availableModules}
            />

            {/* Action buttons */}
            <div className="flex items-center gap-3">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add New Resource
              </Button>
              <Button variant="outline" size="icon">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content based on selected view */}
          <div className="mt-6">
            {selectedView === 'my-resources' && renderMyResourcesView()}
            {selectedView === 'discover-resources' && renderDiscoverResourcesView()}
            {selectedView === 'archived-resources' && renderArchivedResourcesView()}
            {!selectedView && renderMyResourcesView()}
          </div>
        </div>
      </main>
    </div>
  )
}

export default Resources
